// Drawing Tools Engine & Multi-User / Multi-Symbol Persistence

const DRAWING_STORAGE_KEY_PREFIX = "exness_drawings_";
const USER_PROFILES_KEY = "exness_user_profiles";

export const DEFAULT_PROFILES = [
  { id: "trader_main", name: "Trader Chính (Mặc định)", role: "Main Analyst" },
  { id: "trader_scalp", name: "Kế Hoạch Scalping (M1-M5)", role: "Scalper" },
  { id: "trader_swing", name: "Kế Hoạch Swing (H1-D1)", role: "Swing Trader" },
];

export function getUserProfiles() {
  try {
    const saved = localStorage.getItem(USER_PROFILES_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.warn("Load profiles error:", e);
  }
  return DEFAULT_PROFILES;
}

export function saveUserProfiles(profiles) {
  try {
    localStorage.setItem(USER_PROFILES_KEY, JSON.stringify(profiles));
  } catch (e) {
    console.warn("Save profiles error:", e);
  }
}

export function getActiveUserId() {
  try {
    return localStorage.getItem("exness_active_user_id") || "trader_main";
  } catch (e) {
    return "trader_main";
  }
}

export function setActiveUserId(userId) {
  try {
    localStorage.setItem("exness_active_user_id", userId);
  } catch (e) {}
}

export function loadUserDrawings(userId, symbol) {
  const key = `${DRAWING_STORAGE_KEY_PREFIX}${userId}_${symbol}`;
  try {
    const saved = localStorage.getItem(key);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.warn("Load drawings error:", e);
  }
  return [];
}

export function saveUserDrawings(userId, symbol, drawings) {
  const key = `${DRAWING_STORAGE_KEY_PREFIX}${userId}_${symbol}`;
  try {
    localStorage.setItem(key, JSON.stringify(drawings));
  } catch (e) {
    console.warn("Save drawings error:", e);
  }
}

export function clearUserDrawings(userId, symbol) {
  const key = `${DRAWING_STORAGE_KEY_PREFIX}${userId}_${symbol}`;
  try {
    localStorage.removeItem(key);
  } catch (e) {}
}

// Drawing Renderers on HTML5 Canvas
export function renderAllDrawings(ctx, drawings, activeDrawing, selectedId, chart, candleSeries) {
  if (!ctx || !chart || !candleSeries) return;

  try {
    const timeScale = chart.timeScale();
    const allToRender = [...(drawings || [])];
    if (activeDrawing) {
      allToRender.push(activeDrawing);
    }

    for (const item of allToRender) {
      try {
        const isSelected = item.id === selectedId;
        switch (item.type) {
          case "trendline":
            renderTrendline(ctx, item, isSelected, timeScale, candleSeries);
            break;
          case "horizontal":
            renderHorizontalLine(ctx, item, isSelected, candleSeries);
            break;
          case "rectangle":
            renderRectangle(ctx, item, isSelected, timeScale, candleSeries);
            break;
          case "fibonacci":
            renderFibonacci(ctx, item, isSelected, timeScale, candleSeries);
            break;
          case "text":
            renderText(ctx, item, isSelected, timeScale, candleSeries);
            break;
          default:
            break;
        }
      } catch (itemErr) {
        // Suppress drawing error for individual item to prevent chart crash
      }
    }
  } catch (err) {
    // Suppress general error
  }
}

function drawAnchorCircle(ctx, x, y, color = "#38bdf8") {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, 5, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.restore();
}

function renderTrendline(ctx, item, isSelected, timeScale, candleSeries) {
  if (!item.p1 || !item.p2) return;
  const x1 = timeScale.timeToCoordinate(item.p1.time);
  const y1 = candleSeries.priceToCoordinate(item.p1.price);
  const x2 = timeScale.timeToCoordinate(item.p2.time);
  const y2 = candleSeries.priceToCoordinate(item.p2.price);

  if (x1 === null || y1 === null || x2 === null || y2 === null) return;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.strokeStyle = item.color || "#38bdf8";
  ctx.lineWidth = item.width || 2;
  if (item.style === 2) ctx.setLineDash([6, 6]);
  else if (item.style === 1) ctx.setLineDash([2, 4]);
  ctx.stroke();

  if (isSelected) {
    drawAnchorCircle(ctx, x1, y1, item.color);
    drawAnchorCircle(ctx, x2, y2, item.color);
  }
  ctx.restore();
}

function renderHorizontalLine(ctx, item, isSelected, candleSeries) {
  if (item.price === undefined || item.price === null) return;
  const y = candleSeries.priceToCoordinate(item.price);
  if (y === null) return;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(0, y);
  ctx.lineTo(ctx.canvas.width, y);
  ctx.strokeStyle = item.color || "#f59e0b";
  ctx.lineWidth = item.width || 1.5;
  if (item.style === 2) ctx.setLineDash([6, 6]);
  else if (item.style === 1) ctx.setLineDash([2, 4]);
  ctx.stroke();

  // Price tag badge
  const label = item.label ? `${item.label} (${item.price.toFixed(2)})` : item.price.toFixed(2);
  ctx.fillStyle = item.color || "#f59e0b";
  ctx.fillRect(ctx.canvas.width - 90, y - 10, 85, 20);
  ctx.fillStyle = "#090d16";
  ctx.font = "bold 10px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, ctx.canvas.width - 48, y);

  if (isSelected) {
    drawAnchorCircle(ctx, 40, y, item.color);
    drawAnchorCircle(ctx, ctx.canvas.width / 2, y, item.color);
    drawAnchorCircle(ctx, ctx.canvas.width - 110, y, item.color);
  }
  ctx.restore();
}

function renderRectangle(ctx, item, isSelected, timeScale, candleSeries) {
  if (!item.p1 || !item.p2) return;
  const x1 = timeScale.timeToCoordinate(item.p1.time);
  const y1 = candleSeries.priceToCoordinate(item.p1.price);
  const x2 = timeScale.timeToCoordinate(item.p2.time);
  const y2 = candleSeries.priceToCoordinate(item.p2.price);

  if (x1 === null || y1 === null || x2 === null || y2 === null) return;

  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const width = Math.abs(x2 - x1);
  const height = Math.abs(y2 - y1);

  ctx.save();
  ctx.fillStyle = item.fillColor || "rgba(56, 189, 248, 0.15)";
  ctx.fillRect(left, top, width, height);

  ctx.strokeStyle = item.color || "#38bdf8";
  ctx.lineWidth = item.width || 1.5;
  if (item.style === 2) ctx.setLineDash([6, 6]);
  ctx.strokeRect(left, top, width, height);

  if (isSelected) {
    drawAnchorCircle(ctx, left, top, item.color);
    drawAnchorCircle(ctx, left + width, top, item.color);
    drawAnchorCircle(ctx, left, top + height, item.color);
    drawAnchorCircle(ctx, left + width, top + height, item.color);
  }
  ctx.restore();
}

// Authentic TradingView Fibonacci Retracement Renderer
export const FIB_TEMPLATE_STORAGE_KEY = "exness_fib_default_template";

export const TRADINGVIEW_FIB_LEVELS = [
  { ratio: 0.0, color: "#787b86", fill: "rgba(120, 123, 134, 0.12)" },
  { ratio: 0.236, color: "#f23645", fill: "rgba(242, 54, 69, 0.12)" },
  { ratio: 0.382, color: "#ff9800", fill: "rgba(255, 152, 0, 0.12)" },
  { ratio: 0.5, color: "#4caf50", fill: "rgba(76, 175, 80, 0.12)" },
  { ratio: 0.618, color: "#089981", fill: "rgba(8, 153, 129, 0.15)" },
  { ratio: 0.786, color: "#00bcd4", fill: "rgba(0, 188, 212, 0.12)" },
  { ratio: 1.0, color: "#787b86", fill: "rgba(120, 123, 134, 0.12)" },
  { ratio: 1.618, color: "#2962ff", fill: "rgba(41, 98, 255, 0.12)" },
  { ratio: 2.618, color: "#f23645", fill: "rgba(242, 54, 69, 0.12)" },
  { ratio: 3.618, color: "#9c27b0", fill: "rgba(156, 39, 176, 0.12)" },
  { ratio: 4.236, color: "#e91e63", fill: "rgba(233, 30, 99, 0.12)" },
];

export function loadSavedFibTemplate() {
  try {
    const raw = localStorage.getItem(FIB_TEMPLATE_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.warn("Load fib template error:", e);
  }
  return null;
}

export function saveFibTemplate(template) {
  try {
    localStorage.setItem(FIB_TEMPLATE_STORAGE_KEY, JSON.stringify(template));
  } catch (e) {
    console.warn("Save fib template error:", e);
  }
}


function hexToRgba(hex, alpha = 0.12) {
  if (!hex || !hex.startsWith("#")) return `rgba(120, 123, 134, ${alpha})`;
  let r = 120, g = 123, b = 134;
  if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function renderFibonacci(ctx, item, isSelected, timeScale, candleSeries) {
  if (!item.p1 || !item.p2) return;
  const x1 = timeScale.timeToCoordinate(item.p1.time);
  const y1 = candleSeries.priceToCoordinate(item.p1.price);
  const x2 = timeScale.timeToCoordinate(item.p2.time);
  const y2 = candleSeries.priceToCoordinate(item.p2.price);

  if (x1 === null || y1 === null || x2 === null || y2 === null) return;

  const minX = Math.min(x1, x2);
  const maxX = item.extendRight ? ctx.canvas.width : Math.max(x1, x2) + 140;
  const priceDiff = item.p2.price - item.p1.price;

  ctx.save();

  // Active levels (default or customized)
  const sourceLevels = (item.fibLevels && item.fibLevels.length > 0) ? item.fibLevels : TRADINGVIEW_FIB_LEVELS;
  const activeLevels = sourceLevels.filter((l) => l.enabled !== false);
  
  // Sort levels by ratio
  activeLevels.sort((a, b) => a.ratio - b.ratio);

  // 1. Calculate screen coordinates for all active Fibonacci levels (Retracement from p2 towards p1)
  const calculatedLevels = activeLevels.map((lvl) => {
    const lvlPrice = item.p2.price + (item.p1.price - item.p2.price) * lvl.ratio;
    const yCoord = candleSeries.priceToCoordinate(lvlPrice);
    return {
      ...lvl,
      price: lvlPrice,
      y: yCoord,
      fill: hexToRgba(lvl.color, item.backgroundOpacity || 0.12),
    };
  }).filter((l) => l.y !== null);

  // 2. Draw Translucent Colored Background Bands between adjacent levels
  if (item.showBackground !== false) {
    for (let i = 0; i < calculatedLevels.length - 1; i++) {
      const l1 = calculatedLevels[i];
      const l2 = calculatedLevels[i + 1];
      if (l1.y === null || l2.y === null) continue;

      const top = Math.min(l1.y, l2.y);
      const height = Math.abs(l2.y - l1.y);

      ctx.fillStyle = l1.fill;
      ctx.fillRect(minX, top, maxX - minX, height);
    }
  }

  // 3. Draw Trendline connecting Start Anchor to End Anchor
  if (item.showTrendline !== false) {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.strokeStyle = item.trendlineColor || "rgba(148, 163, 184, 0.7)";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // 4. Draw Horizontal Level Lines & Price Tags
  for (const lvl of calculatedLevels) {
    ctx.beginPath();
    ctx.moveTo(minX, lvl.y);
    ctx.lineTo(maxX, lvl.y);
    ctx.strokeStyle = lvl.color;
    ctx.lineWidth = lvl.ratio === 0.618 || lvl.ratio === 0.5 || lvl.ratio === 1.0 || lvl.ratio === 0.0 ? 1.5 : 1;
    ctx.stroke();

    // TradingView Text Label Format
    if (item.showPrices !== false) {
      const priceStr = (lvl.price !== undefined && lvl.price !== null && Number.isFinite(lvl.price)) ? Number(lvl.price).toFixed(2) : "";
      const labelText = priceStr ? `${lvl.ratio} (${priceStr})` : `${lvl.ratio}`;
      ctx.font = "bold 11px monospace";
      ctx.fillStyle = lvl.color;
      ctx.textAlign = "left";
      ctx.textBaseline = "bottom";
      ctx.fillText(labelText, minX + 6, lvl.y - 2);
    }
  }

  // 5. Draw Anchor Handles when selected
  if (isSelected) {
    drawAnchorCircle(ctx, x1, y1, "#2962ff");
    drawAnchorCircle(ctx, x2, y2, "#2962ff");
  }

  ctx.restore();
}

function renderText(ctx, item, isSelected, timeScale, candleSeries) {
  if (!item.p) return;
  const x = timeScale.timeToCoordinate(item.p.time);
  const y = candleSeries.priceToCoordinate(item.p.price);
  if (x === null || y === null) return;

  ctx.save();
  ctx.fillStyle = "rgba(15, 23, 42, 0.85)";
  ctx.strokeStyle = item.color || "#38bdf8";
  ctx.lineWidth = isSelected ? 2 : 1;
  const text = item.text || "Ghi chú";
  ctx.font = "bold 12px sans-serif";
  const metrics = ctx.measureText(text);
  const p = 6;
  ctx.fillRect(x, y - 18, metrics.width + p * 2, 24);
  ctx.strokeRect(x, y - 18, metrics.width + p * 2, 24);

  ctx.fillStyle = item.color || "#38bdf8";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + p, y - 6);

  if (isSelected) {
    drawAnchorCircle(ctx, x, y - 6, item.color);
  }
  ctx.restore();
}
